import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

const WAVE_COUNT = 12;

export function WaveBackground({ scrollProgress }: { scrollProgress: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const { theme } = useTheme();
  const scrollRef = useRef(scrollProgress);
  scrollRef.current = scrollProgress;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let time = 0;

    const isDark = document.documentElement.classList.contains("dark");
    const bgFill = isDark ? "#0a0a0a" : "#FFFFFF";
    const strokeRgb = isDark ? "255, 255, 255" : "0, 0, 0";

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      const scroll = scrollRef.current;

      for (let w = 0; w < WAVE_COUNT; w++) {
        const wFrac = w / WAVE_COUNT;
        const baseY = height * (0.15 + wFrac * 0.7);
        const amplitude = 30 + w * 8 + scroll * 40;
        const frequency = 0.003 + wFrac * 0.002;
        const speed = 0.0003 + wFrac * 0.0001;
        const phase = time * speed + w * 0.8;

        const alpha = 0.04 + (1 - wFrac) * 0.08;
        ctx.strokeStyle = `rgba(${strokeRgb}, ${alpha})`;
        ctx.lineWidth = 1;

        ctx.beginPath();
        for (let x = 0; x <= width; x += 2) {
          const y =
            baseY +
            Math.cos(x * frequency + phase) * amplitude +
            Math.cos(x * frequency * 0.5 + phase * 1.3) * amplitude * 0.4;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      if (!reducedMotion) {
        time += 16;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion, theme]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        mixBlendMode: "var(--bg-blend-mode)" as React.CSSProperties["mixBlendMode"],
      }}
      aria-hidden="true"
    />
  );
}
